import React from 'react';
import {
  Plus, Trash2, ArrowUp, ArrowDown, Users, Utensils, MessageSquare,
  UserPlus, Type, ListChecks, ToggleRight, Hash, Eye, EyeOff, Info,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';

let counter = 0;
const uid = () => `f_${Date.now()}_${counter++}`;

const DEFAULT_POTLUCK_EXPLAINER =
  "We love sharing a meal together! Pick a category and let us know what you'd like to bring "
  + "so we end up with a balanced spread. Not able to bring something? No worries at all \u2014 "
  + "please still come and enjoy. Choose \u201cNot sure yet\u201d and we'll figure out the details.";

const PRESETS = [
  { key: 'guest_count', label: 'How many in your party? (including you)', type: 'number', icon: Hash, name: 'Total party size' },
  { key: 'bringing', label: 'What are you bringing?', type: 'potluck', share_responses: true, icon: Utensils, name: "What are you bringing? (potluck)" },
  { key: 'plus_ones', label: 'Who are you bringing with you?', type: 'text', icon: UserPlus, name: 'Who are you bringing?' },
  { key: 'dietary', label: 'Any dietary restrictions or allergies?', type: 'textarea', icon: Users, name: 'Dietary restrictions' },
  { key: 'message', label: 'Anything to share with the host?', type: 'textarea', icon: MessageSquare, name: 'Message to the host' },
];

const CUSTOM_TYPES = [
  { type: 'text', icon: Type, name: 'Short text' },
  { type: 'yesno', icon: ToggleRight, name: 'Yes / No' },
  { type: 'choice', icon: ListChecks, name: 'Multiple choice' },
];

const TYPE_LABEL = { text: 'Short text', textarea: 'Long text', number: 'Number', yesno: 'Yes / No', choice: 'Multiple choice', potluck: 'Potluck dishes' };

export const RsvpBuilder = ({ fields, onChange }) => {
  const list = fields || [];

  const update = (id, patch) => onChange(list.map((f) => (f._id === id ? { ...f, ...patch } : f)));
  const remove = (id) => onChange(list.filter((f) => f._id !== id));
  const move = (idx, dir) => {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const addPreset = (p) => {
    if (list.some((f) => f.key === p.key)) return;
    const base = { _id: uid(), key: p.key, label: p.label, type: p.type, required: false, options: [], share_responses: !!p.share_responses };
    if (p.type === 'potluck') {
      base.categories = ['Main dish', 'Side item'];
      base.allow_unsure = true;
      base.explainer = DEFAULT_POTLUCK_EXPLAINER;
    }
    onChange([...list, base]);
  };
  const addCustom = (t) => {
    onChange([...list, { _id: uid(), key: `custom_${uid()}`, label: '', type: t.type, required: false, options: t.type === 'choice' ? ['Option 1', 'Option 2'] : [], share_responses: false }]);
  };

  return (
    <div data-testid="rsvp-builder">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display text-lg font-semibold">RSVP form</h3>
          <p className="text-sm text-muted-foreground">Guests always choose Yes / Maybe / No. Add the questions you want them to answer.</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" className="gap-2" data-testid="add-rsvp-field-button"><Plus className="h-4 w-4" /> Add question</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Quick add</DropdownMenuLabel>
            {PRESETS.map((p) => {
              const Icon = p.icon;
              const added = list.some((f) => f.key === p.key);
              return (
                <DropdownMenuItem key={p.key} disabled={added} onClick={() => addPreset(p)} data-testid={`preset-${p.key}`}>
                  <Icon className="h-4 w-4 mr-2 text-muted-foreground" /> {p.name}{added && <span className="ml-auto text-xs text-muted-foreground">added</span>}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Custom question</DropdownMenuLabel>
            {CUSTOM_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <DropdownMenuItem key={t.type} onClick={() => addCustom(t)} data-testid={`custom-${t.type}`}>
                  <Icon className="h-4 w-4 mr-2 text-muted-foreground" /> {t.name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No extra questions yet. Guests will just confirm if they&apos;re coming. Add a question to collect more.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((f, idx) => {
            const canShare = f.type === 'text' || f.type === 'textarea' || f.type === 'potluck';
            return (
              <div key={f._id} className="rounded-xl border border-border bg-card p-4" data-testid="rsvp-field-row">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-0.5 pt-1">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="font-normal">{TYPE_LABEL[f.type] || f.type}</Badge>
                    </div>
                    <Input value={f.label} onChange={(e) => update(f._id, { label: e.target.value })} placeholder="Question label (e.g. What are you bringing?)" className="h-10" data-testid="rsvp-field-label-input" />

                    {f.type === 'choice' && (
                      <div className="mt-3">
                        <Label className="text-xs text-muted-foreground">Choices</Label>
                        <div className="space-y-2 mt-1.5">
                          {(f.options || []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <Input value={opt} onChange={(e) => { const o = [...f.options]; o[oi] = e.target.value; update(f._id, { options: o }); }} className="h-9" placeholder={`Option ${oi + 1}`} />
                              <Button type="button" size="icon" variant="ghost" onClick={() => update(f._id, { options: f.options.filter((_, i) => i !== oi) })}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          ))}
                          <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => update(f._id, { options: [...(f.options || []), `Option ${(f.options?.length || 0) + 1}`] })}><Plus className="h-3.5 w-3.5" /> Add choice</Button>
                        </div>
                      </div>
                    )}

                    {f.type === 'potluck' && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Dish categories (guests pick one per item)</Label>
                          <div className="space-y-2 mt-1.5">
                            {(f.categories || []).map((cat, ci) => (
                              <div key={ci} className="flex items-center gap-2">
                                <Input value={cat} onChange={(e) => { const c = [...f.categories]; c[ci] = e.target.value; update(f._id, { categories: c }); }} className="h-9" placeholder={`Category ${ci + 1}`} data-testid="potluck-category-input" />
                                <Button type="button" size="icon" variant="ghost" onClick={() => update(f._id, { categories: f.categories.filter((_, i) => i !== ci) })}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                            ))}
                            <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={() => update(f._id, { categories: [...(f.categories || []), ''] })} data-testid="add-potluck-category"><Plus className="h-3.5 w-3.5" /> Add category</Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Explainer shown to guests (why a potluck, it&apos;s ok to come empty-handed)</Label>
                          <Textarea value={f.explainer || ''} onChange={(e) => update(f._id, { explainer: e.target.value })} rows={3} className="mt-1.5" placeholder="Tell guests why you're doing a potluck and that it's fine to come without a dish." data-testid="potluck-explainer-input" />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch checked={f.allow_unsure} onCheckedChange={(v) => update(f._id, { allow_unsure: v })} data-testid="potluck-allow-unsure-toggle" />
                          <span className="text-sm">Offer a &ldquo;Not sure yet&rdquo; option</span>
                        </label>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch checked={f.required} onCheckedChange={(v) => update(f._id, { required: v })} data-testid="rsvp-field-required-toggle" />
                        <span className="text-sm">Required</span>
                      </label>
                      {canShare && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch checked={f.share_responses} onCheckedChange={(v) => update(f._id, { share_responses: v })} data-testid="rsvp-field-share-toggle" />
                          <span className="text-sm flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Show answers to all guests</span>
                        </label>
                      )}
                      {canShare && f.share_responses && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch checked={f.share_anonymous} onCheckedChange={(v) => update(f._id, { share_anonymous: v })} data-testid="rsvp-field-anonymous-toggle" />
                          <span className="text-sm flex items-center gap-1"><EyeOff className="h-3.5 w-3.5" /> Hide guest names</span>
                        </label>
                      )}
                    </div>
                  </div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => remove(f._id)} className="text-destructive flex-shrink-0" data-testid="rsvp-field-remove"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Helpers to attach/detach internal _id used by the builder
export const withFieldIds = (fields) => (fields || []).map((f, i) => ({ ...f, _id: f._id || `f_init_${i}` }));
export const stripFieldIds = (fields) => (fields || []).map(({ _id, ...rest }) => ({
  ...rest,
  options: rest.options || [],
  categories: rest.categories || [],
  allow_unsure: !!rest.allow_unsure,
  explainer: rest.explainer || '',
  share_responses: !!rest.share_responses,
  share_anonymous: !!rest.share_anonymous,
  required: !!rest.required,
}));
